import {
  allPlanColumns,
  unsupportedColumns,
  type ColumnRef,
  type ImportPlan,
  type ImportProfileSummary,
  type ImportSheetSummary,
  type PlanColumn,
} from '@ni/shared';
import {
  EXCLUSION_RULE_LABELS,
  IMPORT_DOCUMENT_BODY_COLUMN,
  IMPORT_GLANCE_LABELS,
  IMPORT_GLANCE_NONE,
  importGoesOntoLine,
  IMPORT_GLANCE_TRACKED_ONLY,
  RESOLUTION_LEVEL_LABELS,
  ROW_EVENT_ROLE_LABELS,
  SEAT_PART_LABELS,
  planNameRuleWords,
  subjectStatusPlural,
} from '../../lib/copy';
import { formatCount, joinClauses, plural } from '../../lib/format';

/**
 * **The plan at a glance** (import note §1.7, the 2026-09-06 design pass).
 *
 * The Plan tab was nine small-caps sections deep — columns, levels, source,
 * name, exclusions, identifiers, record fields, bin, joins, occasion — and the
 * office's first question about any of them is the same one: *what will this do
 * to my people?* Nine headings is not an answer to that; it is nine places to
 * go and look for one.
 *
 * So the tab opens with six or seven lines that are the answer, computed here
 * from the plan and the profile together. Every one of them is a sentence
 * somebody could read out: how many people, what they become, where their names
 * come from, which prose gets read, what is skipped and how much of it, what the
 * file is about, and whether a second sheet rides along.
 *
 * **It is derived, never stored.** The plan is the document; this is a reading
 * of it, so a plan the planner rewrites reads differently on the next paint with
 * nothing to keep in step.
 */

export interface GlanceLine {
  /** One of {@link IMPORT_GLANCE_LABELS} — fixed words, fixed order. */
  readonly label: string;
  readonly value: string;
}

/**
 * The lines, in order, with the ones this file has nothing to say for left out
 * entirely — a "Second sheet: none" on a one-sheet file is a line that exists
 * only to be read past.
 */
export function planGlance(
  plan: ImportPlan,
  profile: ImportProfileSummary | undefined,
  vertical: string,
  /** An attendee list's event, said first: where its people go is the question. */
  eventName?: string,
): GlanceLine[] {
  const sheet = sheetFor(profile, plan.source.sheet);

  return [
    line(
      IMPORT_GLANCE_LABELS.goesOnto,
      eventName === undefined ? null : importGoesOntoLine(eventName),
    ),
    line(IMPORT_GLANCE_LABELS.people, peopleLine(plan, profile, sheet)),
    line(IMPORT_GLANCE_LABELS.become, becomeLine(plan, vertical)),
    line(IMPORT_GLANCE_LABELS.names, namesLine(plan)),
    line(IMPORT_GLANCE_LABELS.facts, columnsOfKind(plan, 'fact')),
    line(IMPORT_GLANCE_LABELS.kept, columnsOfKind(plan, 'record_field')),
    line(IMPORT_GLANCE_LABELS.relationships, columnsOfKind(plan, 'relation')),
    line(IMPORT_GLANCE_LABELS.seats, seatsLine(plan)),
    line(IMPORT_GLANCE_LABELS.notes, notesLine(plan)),
    line(IMPORT_GLANCE_LABELS.leftOut, leftOutLine(plan)),
    line(IMPORT_GLANCE_LABELS.notImportable, notImportableLine(plan)),
    line(IMPORT_GLANCE_LABELS.occasion, occasionLine(plan)),
    line(IMPORT_GLANCE_LABELS.secondSheet, joinLine(plan)),
  ].filter((one): one is GlanceLine => one !== null);
}

const line = (label: string, value: string | null): GlanceLine | null =>
  value === null || value.length === 0 ? null : { label, value };

/** The sheet this plan reads, or the only one, or none at all on a document. */
export const sheetFor = (
  profile: ImportProfileSummary | undefined,
  name: string | undefined,
): ImportSheetSummary | undefined =>
  profile?.sheets.find((candidate) => candidate.name === name) ?? profile?.sheets[0];

/**
 * **How many people, out of what.**
 *
 * A document counts headings and a grid counts rows, and the two arithmetics
 * are genuinely different: a couple heading is one entry and two people, so a
 * programme of 146 entries produces 226 records and a line saying 146 would be
 * wrong about the thing the office is about to do.
 */
function peopleLine(
  plan: ImportPlan,
  profile: ImportProfileSummary | undefined,
  sheet: ImportSheetSummary | undefined,
): string | null {
  const document = profile?.document;
  if (document) {
    const headings = Math.max(document.heading_count - document.non_name_headings, 0);
    const couples = plan.identity.split === 'couple_heading' ? document.couple_headings : 0;
    const people = headings + couples;
    return joinClauses([
      `${formatCount(people)} from ${plural(headings, 'heading', 'headings')}`,
      couples > 0 ? `${plural(couples, 'couple', 'couples')} split into two` : null,
    ]);
  }

  if (!sheet) return null;
  const groups = sheet.group_titles.length;
  /* §1.16a: the grid's own version of the same arithmetic. A name cell holding
     two people is one row and two records, so a file of 119 rows with 14 couples
     in the name column produces 133 people — and only where the plan says to
     split them, which is the office's own decision to see on this line. */
  const couples = plan.identity.split === 'couple_cell' ? coupleCells(plan, sheet) : 0;
  const people = sheet.data_row_count + couples;
  return joinClauses([
    groups > 0
      ? `${formatCount(people)} people in ${plural(groups, 'group', 'groups')}`
      : `${formatCount(people)} people`,
    couples > 0 ? `${plural(couples, 'couple', 'couples')} split into two` : null,
  ]);
}

/**
 * **How many of the name column's cells name two people**, as the profile
 * counted them. Zero where the plan takes its names from anywhere but one
 * column: two columns joined are a first name and a last name, and a heading is
 * not a grid's at all.
 */
function coupleCells(plan: ImportPlan, sheet: ImportSheetSummary): number {
  if (plan.identity.name.kind !== 'column') return 0;
  const index = plan.identity.name.column.index;
  return sheet.columns.find((column) => column.index === index)?.couple_cells ?? 0;
}

/**
 * **Where each name comes from**, which on a file of occasions is not one place:
 * `identity.name` is not read at all there, and each people column is its own
 * name source. A line that went on naming the plan's name rule would be naming
 * the one key such a plan does not use.
 */
function namesLine(plan: ImportPlan): string {
  const rowEvent = plan.row_event;
  if (!rowEvent) return planNameRuleWords(plan.identity.name);
  return rowEvent.people
    .map((person) => `${columnName(person.column)} as ${ROW_EVENT_ROLE_LABELS[person.role]}`)
    .join(', ');
}

/**
 * **What the file is about** — one occasion everybody on it took part in, or an
 * occasion per row, or neither.
 *
 * The two are different statements and the line says which: `occasion` is "this
 * whole file is the Medici Day programme", and `row_event` is "each row of this
 * file is an evening of its own". A plan never carries both.
 */
function occasionLine(plan: ImportPlan): string {
  const rowEvent = plan.row_event;
  if (!rowEvent) return plan.occasion?.name ?? IMPORT_GLANCE_NONE;
  const name =
    typeof rowEvent.occasion.name === 'string'
      ? rowEvent.occasion.name
      : columnName(rowEvent.occasion.name);
  /* Commas rather than the glance's usual middle dot: this line is one sentence
     about the file — "each row is an occasion: Reservation, on Date" — where the
     others are lists of clauses, and a dot in the middle of a sentence reads as
     two half-sentences. */
  return [
    `each row is an occasion: ${name}`,
    rowEvent.occasion.date ? `on ${columnName(rowEvent.occasion.date)}` : null,
  ]
    .filter((one): one is string => one !== null)
    .join(', ');
}

/** What everyone arrives as, and how many groups are an exception to it. */
function becomeLine(plan: ImportPlan, vertical: string): string {
  /* **On a file of occasions the level is the column's**, so the line reads off
     the people rather than off `resolution.default`, which such a plan does not
     use at all: the member is somebody the club tracks and the guest is somebody
     it now knows of, and one word could not say both. */
  const rowEvent = plan.row_event;
  if (rowEvent) {
    return rowEvent.people
      .map((person) => {
        const level = RESOLUTION_LEVEL_LABELS[person.level];
        const status =
          person.level === 'subject'
            ? subjectStatusPlural(vertical, person.status ?? plan.status.default)
            : null;
        return status
          ? `${ROW_EVENT_ROLE_LABELS[person.role]}: ${level}, as ${status}`
          : `${ROW_EVENT_ROLE_LABELS[person.role]}: ${level}`;
      })
      .join('; ');
  }

  /* The plural, because the line is about everybody on the file: "Tracked, as
     prospects" rather than "as Prospect", which reads as one person's status
     rather than as what six hundred names arrive at. */
  const status = subjectStatusPlural(vertical, plan.status.default);
  const rules = glanceRuleCount(plan);
  return joinClauses([
    `${RESOLUTION_LEVEL_LABELS[plan.resolution.default]}, as ${status}`,
    rules > 0 ? `${plural(rules, 'rule', 'rules')} by group` : null,
  ]);
}

/**
 * The exceptions, counted as the office would count them: one rule per group,
 * not one per key. A group that gets both a level and a status is one exception
 * to the line above it, and the editable table below renders it as one row.
 */
export const glanceRuleCount = (plan: ImportPlan): number =>
  new Set([...plan.resolution.rules.map(matchKey), ...plan.status.rules.map(matchKey)]).size;

export const matchKey = (rule: { by: string; equals: string; column?: ColumnRef }): string =>
  `${rule.by}|${rule.column?.index ?? ''}|${rule.equals}`;

/**
 * **The columns that become one kind of thing, by their own headers** — the
 * answer the column table only gave one row at a time. Joined sheets count too,
 * as they do for the notes line: they write the same people.
 */
function columnsOfKind(
  plan: ImportPlan,
  kind: 'fact' | 'record_field' | 'relation',
): string | null {
  const names = allPlanColumns(plan)
    .filter((column) => column.target.kind === kind)
    .map((column) => columnName(column.column));
  if (names.length === 0) return null;
  /* A known person has no record to keep a detail on, so the commit writes
     these for tracked people only, and a plan that makes anybody known says so. */
  return kind === 'record_field' && !everyoneTracked(plan)
    ? joinClauses([names.join(', '), IMPORT_GLANCE_TRACKED_ONLY])
    : names.join(', ');
}

/** "Section (section), Row (row), Seat (seat number)": the seating columns and the part each holds. */
function seatsLine(plan: ImportPlan): string | null {
  const seats = plan.columns.flatMap((column) =>
    column.target.kind === 'seat'
      ? [`${columnName(column.column)} (${SEAT_PART_LABELS[column.target.part].toLowerCase()})`]
      : [],
  );
  return seats.length ? seats.join(', ') : null;
}

/**
 * Whether nobody this plan writes arrives known, by default, rule or column. A
 * set-aside row is written nowhere, so it does not count against the line.
 */
const everyoneTracked = (plan: ImportPlan): boolean =>
  plan.row_event
    ? plan.row_event.people.every((person) => person.level !== 'known')
    : plan.resolution.default !== 'known' &&
      plan.resolution.rules.every((rule) => rule.level !== 'known');

/**
 * **Which prose is read at import and which is kept to be read later**, which
 * is the difference between a column that costs money and one that does not.
 */
function notesLine(plan: ImportPlan): string | null {
  const columns = allPlanColumns(plan);
  const read: string[] = [];
  const kept: string[] = [];
  for (const column of columns) {
    const target = column.target;
    if (target.kind !== 'prose') continue;
    (readsAtImport(column) ? read : kept).push(proseName(plan, column.column));
  }
  if (read.length === 0 && kept.length === 0) return null;

  return joinClauses([
    read.length > 0 ? `${read.join(', ')} read now` : null,
    kept.length > 0 ? `${kept.join(', ')} kept to read later` : null,
  ]);
}

/** The columns whose notes are read for facts, named as the notes line names them. */
export function noteColumnsOf(plan: ImportPlan): string[] {
  return allPlanColumns(plan)
    .filter(readsAtImport)
    .map((column) => proseName(plan, column.column));
}

/** A document's one column, in the words a person reads it in. */
const proseName = (plan: ImportPlan, ref: ColumnRef): string =>
  plan.source.record_rule.kind === 'heading' && ref.header === 'body'
    ? IMPORT_DOCUMENT_BODY_COLUMN
    : columnName(ref);

/**
 * A prose column is read at import when it says so — and a routed column, whose
 * kind is chosen per cell, is read when *any* of its routes is, because the
 * question this answers is whether the column costs anything.
 */
export function readsAtImport(column: PlanColumn): boolean {
  const target = column.target;
  if (target.kind !== 'prose') return false;
  if (typeof target.prose_kind !== 'string') {
    return target.extract || target.prose_kind.by_prefix.some((route) => route.extract);
  }
  return target.extract;
}

/** Ignored columns are named up to this many; past it the line gives a count. */
const LEFT_OUT_NAMED_MAX = 3;

/**
 * **What the file holds that will not be written**, summed, with the biggest
 * kinds named. Both halves: rows the record rule skips, and columns the plan
 * ignores — the office asked for the file to be imported, and both of these are
 * the plan quietly not doing that.
 */
function leftOutLine(plan: ImportPlan): string | null {
  const exclusions = [
    ...plan.source.exclusions,
    ...(plan.joins ?? []).flatMap((join) => join.source.exclusions),
  ];
  const rows = exclusions.reduce((total, one) => total + one.count, 0);

  const byKind = new Map<string, number>();
  for (const one of exclusions) {
    const label = EXCLUSION_RULE_LABELS[one.rule];
    byKind.set(label, (byKind.get(label) ?? 0) + one.count);
  }
  const largest = [...byKind]
    .sort((left, right) => right[1] - left[1])
    .slice(0, 2)
    .map(([label, count]) => `${formatCount(count)} ${label}`);

  /* Named while there are few enough to read at a glance: "1 column" gave
     somebody whose file has a Notes column no way to see that it was the one
     left out. */
  const ignored = plan.columns
    .filter((column) => column.target.kind === 'ignore')
    .map((column) => columnName(column.column));
  const ignoredLine =
    ignored.length === 0
      ? null
      : ignored.length <= LEFT_OUT_NAMED_MAX
        ? `${plural(ignored.length, 'column', 'columns')} (${ignored.join(', ')})`
        : plural(ignored.length, 'column', 'columns');

  const clauses = [
    rows > 0
      ? `${plural(rows, 'row', 'rows')}${largest.length > 0 ? ` (${largest.join(', ')})` : ''}`
      : null,
    ignoredLine,
  ].filter((one): one is string => one !== null);

  return clauses.length === 0 ? IMPORT_GLANCE_NONE : clauses.join(' and ');
}

/**
 * **How many columns nothing here can hold yet** (owner's ruling, 2026-09-07),
 * or nothing at all — which is the ordinary case and reads as an absent line.
 *
 * Deliberately a count and not a list: the section below the glance names every
 * one of them with the planner's own sentence, and a glance line that grew with
 * the file would be the one line here that could not be read at a glance. It is
 * the count the planner's own message says too, off the same definition
 * (`unsupportedColumns` in the contract), so the message and the page cannot
 * come out different.
 *
 * `leftOutLine` above counts `ignore` targets and only those, which is what
 * keeps the two lines saying two different things: what the plan chose to skip,
 * and what it could not carry.
 */
function notImportableLine(plan: ImportPlan): string | null {
  const count = unsupportedColumns(plan).length;
  return count === 0 ? null : plural(count, 'column', 'columns');
}

/** The second sheet, and the key it finds its person by. */
function joinLine(plan: ImportPlan): string | null {
  const joins = plan.joins ?? [];
  if (joins.length === 0) return null;
  return joins
    .map(
      (join) =>
        `${join.source.sheet ?? 'a second sheet'}, joined where ` +
        `${columnName(join.on.column)} matches ${columnName(join.on.matches)}`,
    )
    .join('; ');
}

/** A column's own words, or its number where the file gave it none. */
export const columnName = (ref: ColumnRef): string =>
  ref.header.length > 0 ? ref.header : `column ${ref.index}`;
